import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { SyncType } from '@rocksoft/apple-sync-sdk';
import { DeviceState as AppManagementDeviceState } from '@rocksoft/appmanagement-sdk';
import {
  AppleDevice,
  DeviceState as DeviceStateEnum,
  EnableLostModeCommand,
  DeviceDto as LocationsDevice,
  ModelNameFlags,
} from '@rocksoft/locations-sdk';
import { Device as MdmDevice } from '@rocksoft/mdm-sdk-v2';
import { CategoryType, ErrorModel, Wallpaper } from '@rocksoft/shared';
import { LocationSubset } from './models/location-subset';

// ============================================================================
// RESOLVER ACTIONS
// ============================================================================

export const DeviceResolverActions = createActionGroup({
  source: 'Device Resolver',
  events: {
    // From device-store
    'Devices requested': props<{ categoryId: string }>(),
    'Devices for state requested': props<{ state: DeviceStateEnum }>(),
    // From apple-device-store
    'Apple Device requested': props<{ udid: string }>(),
    // From device-application-state
    'Device Application States requested': emptyProps(),
    // From mdm-devices
    'Mdm Devices requested': emptyProps(),
    'Mdm Device requested': props<{ udid: string }>(),
  },
});

// ============================================================================
// API ACTIONS - Partial Updates
// ============================================================================

export const DeviceApiActions = createActionGroup({
  source: 'Device API',
  events: {
    // Partial data loads
    'Basic device info loaded': props<{ devices: LocationsDevice[] }>(),
    'Basic device info load error': props<{ error: ErrorModel }>(),
    'Apple device info loaded': props<{ appleDevice: AppleDevice }>(),
    'Apple device info load error': props<{ error: ErrorModel }>(),
    'Application state loaded': props<{ states: AppManagementDeviceState[] }>(),
    'Application state load error': props<{ error: ErrorModel }>(),
    'Mdm device info loaded': props<{ devices: MdmDevice[] }>(),
    'Mdm device info load error': props<{ error: ErrorModel }>(),

    'Category assignment succeeded': props<{ device: LocationsDevice | undefined }>(),
    'Category assignment failed': props<{ deviceId: string; error: ErrorModel }>(),

    // Sync operations
    'Sync retried': props<{ udid: string }>(),
    'Device sync retry error': props<{ error: ErrorModel }>(),
    'Given sync retried': props<{ udid: string; syncType: SyncType }>(),
    'Device given sync retry error': props<{ error: ErrorModel }>(),
    'Sync devices request successful': props<{ udids: string[]; syncTypes: SyncType[] }>(),
    'OS update retried': props<{ udid: string }>(),
    'Device os update retry error': props<{ error: ErrorModel }>(),
    'OS update continued': props<{ udid: string }>(),
    'Device os update continue error': props<{ error: ErrorModel }>(),

    // Restart operations
    'Device Restart successful': emptyProps(),
    'Failed to restart device': props<{ error: ErrorModel }>(),
    'Restart devices request successful': emptyProps(),
    'Restart devices error': props<{ error: ErrorModel }>(),

    // Lost mode operations (from apple-device-store)
    'Device put in Lost Mode': props<{ udid: string }>(),
    'Failed to put device into Lost Mode': props<{ error: ErrorModel }>(),
    'Device taken out of Lost Mode': props<{ udid: string }>(),
    'Failed to take device out of Lost Mode': props<{ error: ErrorModel }>(),

    // Device name
    'Update device name override succeeded': props<{ device: LocationsDevice }>(),
    'Update device name override failed': props<{ error: ErrorModel }>(),
  },
});

// ============================================================================
// SIGNALR ACTIONS
// ============================================================================

export const DeviceSignalRHubActions = createActionGroup({
  source: 'Device SignalR Hub',
  events: {
    // From device-store
    'Device changed': props<{ device: LocationsDevice }>(),
    'Device removed': props<{ device: LocationsDevice }>(),
    // From mdm-devices
    'Mdm Device changed': props<{ device: MdmDevice }>(),
    'Mdm Device removed': props<{ device: MdmDevice }>(),
  },
});

// ============================================================================
// VIEW ACTIONS
// ============================================================================

export const DeviceAssignmentViewActions = createActionGroup({
  source: 'Device assignment view',
  events: {
    'Category assigned': props<{ deviceId: string; categoryId: string | undefined }>(),
  },
});

export const DeviceSyncStatusViewActions = createActionGroup({
  source: 'Device Sync Status view',
  events: {
    'Retry sync': props<{ udid: string; forceSync: boolean }>(),
    'Retry given sync': props<{ syncType: SyncType; forceSync: boolean }>(),
    'Retry os update': props<{ udid: string }>(),
    'Continue os update': props<{ udid: string }>(),
  },
});

export const DevicesSyncModalActions = createActionGroup({
  source: 'Devices Sync Modal',
  events: {
    'Sync devices requested': props<{ locationSubset: LocationSubset; syncTypes: SyncType[]; force: boolean }>(),
  },
});

export const CategorySettingsWallpaperViewActions = createActionGroup({
  source: 'Category Settings Wallpaper View',
  events: {
    'Wallpaper command sent': props<{
      wallpaper: Wallpaper;
      categoryType?: CategoryType;
      categoryId: string;
    }>(),
  },
});

export const DeviceApiWallpaperActions = createActionGroup({
  source: 'Device API Wallpaper',
  events: {
    'Wallpaper successfully set': emptyProps(),
    'Wallpaper failed to set': props<{ error: ErrorModel }>(),
  },
});

export const DeviceDetailsContextMenuActions = createActionGroup({
  source: 'Device details context menu',
  events: {
    'Device Restart command sent': props<{ udid: string }>(),
    'Device Lost Mode command sent': props<{ udid: string; enableLostModeCommand: EnableLostModeCommand }>(),
    'Device Disable Lost Mode command sent': props<{ udid: string }>(),
  },
});

export const DeviceModalActions = createActionGroup({
  source: 'Device Modal',
  events: {
    'Reboot devices requested': props<{ locationSubset: LocationSubset; modelNames: Set<ModelNameFlags> }>(),
  },
});

export const LocationViewActions = createActionGroup({
  source: 'Location view',
  events: {
    'Export devices': emptyProps(),
  },
});

export const BrandViewActions = createActionGroup({
  source: 'Brand view',
  events: {
    'Export devices': emptyProps(),
  },
});

export const GatewayApiActions = createActionGroup({
  source: 'Gateway API',
  events: {
    'Export devices success': emptyProps(),
    'Export devices failed': props<{ error: ErrorModel }>(),
  },
});

export const DeviceActionsActions = createActionGroup({
  source: 'Device Actions',
  events: {
    'Update device name override': props<{ deviceId: string; newName: string }>(),
  },
});
